import { useState, useCallback } from 'react';
import {
  Container,
  Stack,
  Typography,
  Grid,
  Button,
  Tooltip,
  IconButton,
  Dialog,
  DialogContent,
  DialogContentText,
  DialogActions
} from '@mui/material';
import { Box, styled } from '@mui/system';
import { useNavigate } from 'react-router-dom';
import { BiCloudUpload, BiCloudDownload } from 'react-icons/bi';
import { AiFillEdit } from 'react-icons/ai';
import { CSVDownload } from 'react-csv';
import BrudCrumbs from '../../components/BreadCrumbs';
import Page from '../../components/Page';
import PageTitle from '../../components/PageHeading';
import Scrollbar from '../../components/Scrollbar';
import prospectService from '../services/prospectService';
import Loader from '../Global/Loader';
import ApiErrorMessage from '../Global/ApiErrorMessage';
import UploadLeadProspectTable from './UploadLeadProspectTable';
import useLoginSelector from '../../store/Slice/Login/LoginSelector';

const { DownloadProspectTemplate, ProspectUpload } = prospectService;

function UploadLeadProspect() {
  const navigate = useNavigate();
  const { LoginData } = useLoginSelector();
  const [showTable, setShowTable] = useState(false);
  const [uploadFile, setUploadFile] = useState(null);
  const [template, setTemplate] = useState({ headers: null, data: null });
  const [ui, setUi] = useState({
    open: false,
    msg: '',
    fileName: '',
    loader: false,
    apiError: false,
    errorMsg: ''
  });

  const handleDownloadTemplate = useCallback(async () => {
    const res = await DownloadProspectTemplate(LoginData?.jwt);
    setTemplate({ headers: res.headers, data: res.data });
  }, [LoginData]);

  const handleFileUpload = useCallback((e) => {
    const file = e.target.files[0];
    if (!file) return;

    const extension = file.name.split('.').pop().toLowerCase();
    if (['xlsx', 'csv', 'xls'].includes(extension)) {
      setUploadFile(file);
      setUi((prev) => ({ ...prev, fileName: file.name }));
    } else {
      setUi((prev) => ({ ...prev, open: true, msg: 'Please add only xlsx/csv/xls file only' }));
    }
  }, []);

  return (
    <Page title="Kpros by Kalolytic Solutions || Prospect">
      <Container maxWidth="xl">
        <Box pt={3} pb={3} mb={4}>
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            className="pageHeading"
          >
            <PageTitle info="Upload Leads" />
            <Box>
              <BrudCrumbs />
            </Box>
          </Stack>

          <Scrollbar>
            <Grid container alignItems="stretch" spacing={3} mb={1}>
              <Grid item xs={12} md={8}>
                <Button
                  variant="outlined"
                  component="label"
                  fullWidth
                  sx={{
                    py: 4,
                    border: '2.5px dashed #cbd5e1',
                    color: '#64748b',
                    '&:hover': {
                      bgcolor: '#f8fafc',
                      border: '2.5px dashed #13519d',
                      color: '#13519d'
                    },
                    textTransform: 'none',
                    borderRadius: '12px',
                    flexDirection: 'column',
                    gap: 1.5,
                    position: 'relative',
                    height: '100%'
                  }}
                >
                  <BiCloudUpload size={40} style={{ marginBottom: 4 }} />
                  <Typography
                    sx={{
                      fontWeight: 800,
                      fontSize: '1.1rem',
                      color: ui.fileName ? '#13519d' : 'inherit'
                    }}
                  >
                    {ui.fileName || 'Select Supporting File'}
                  </Typography>
                  <Typography sx={{ fontSize: '0.85rem' }}>
                    Supported file types: .xls, .xlsx, .csv
                  </Typography>
                  <input
                    accept=".xlsx, .xls, .csv"
                    hidden
                    id="contained-button-file"
                    multiple
                    type="file"
                    onChange={(e) => {
                      handleFileUpload(e);
                      e.target.value = null;
                    }}
                  />
                </Button>
              </Grid>
              <Grid item xs={12} md={4}>
                <Button
                  variant="contained"
                  onClick={handleDownloadTemplate}
                  sx={{
                    bgcolor: '#f8fafc',
                    color: '#13519d',
                    border: '1px solid #13519d',
                    borderRadius: '12px',
                    textTransform: 'none',
                    px: 3,
                    py: 1.5,
                    boxShadow: 'none',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 1.5,
                    width: '100%',
                    height: '100%',
                    minHeight: { xs: '120px', md: 'auto' },
                    '&:hover': { bgcolor: '#e2f0fd', boxShadow: 'none' }
                  }}
                >
                  <BiCloudDownload size={32} />
                  <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                    Download Template
                  </Typography>
                </Button>
              </Grid>
            </Grid>

            <Box textAlign="right" mb={3} mt={2}>
              <Button
                variant="contained"
                onClick={() => setShowTable(true)}
                sx={{
                  bgcolor: '#13519d',
                  borderRadius: '10px',
                  textTransform: 'none',
                  fontWeight: 700,
                  px: 4,
                  py: 1,
                  boxShadow: '0 4px 14px rgba(13,70,137,0.25)',
                  '&:hover': { bgcolor: '#0a3870' }
                }}
              >
                Submit File
              </Button>
            </Box>
            {showTable && <UploadLeadProspectTable />}
          </Scrollbar>

          <Dialog open={ui.open} onClose={() => setUi((prev) => ({ ...prev, open: false }))}>
            <DialogContent>
              <DialogContentText>{ui.msg}</DialogContentText>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setUi((prev) => ({ ...prev, open: false }))}>Retry</Button>
            </DialogActions>
          </Dialog>
          <Loader openLoader={ui.loader} />
        </Box>
        {template.headers && (
          <CSVDownload headers={template.headers} data={template.data} target="_blank" />
        )}
        <ApiErrorMessage
          openApiError={ui.apiError}
          setOpenApiError={(val) => setUi((prev) => ({ ...prev, apiError: val }))}
          errorMessage={ui.errorMsg}
        />
      </Container>
    </Page>
  );
}

export default UploadLeadProspect;
